// Inspiration from: https://codepen.io/ahmadawais/pen/WNrOPwO
import React from 'react';

import { Box, styled } from '@mui/material';
import { BoxProps } from '@mui/material/Box';

const ConfettiContainer = styled(Box)`
  overflow: hidden;
`;

type Props = {
  count: number;
  rate: number;
} & BoxProps;

const Confetti: React.FC<Props> = React.memo(({ count, rate, ...boxProps }) => {
  const confettis = new Array(count).fill(count);
  return (
    <ConfettiContainer {...boxProps}>
      {confettis.map((_, index) => (
        <ConfettiItem key={index} index={index} rate={rate} /> // eslint-disable-line
      ))}
    </ConfettiContainer>
  );
});

Confetti.displayName = 'Confetti';

const randomNum = (num: number) => Math.floor(Math.random() * (num + 1));

const shapeStyle = (shape: string, size: number, color: string) => {
  switch (shape) {
    case 'circle':
      return `
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background-color: ${color};
      `;
    case 'triangle':
      return `
        width: 0;
        height: 0;
        border-left: ${size / 2}px solid transparent;
        border-right: ${size / 2}px solid transparent;
        border-bottom: ${size}px solid ${color};
      `;
    case 'wavy':
      return `
        width: ${size}px;
        height: ${size * 0.4}px;
        border-radius: ${size * 0.2}px;
        background-color: ${color};
      `;
    case 'swyftxBird':
      return `
        width: ${size * 1.5}px;
        height: ${size * 1.5}px;
        background-image: url("data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%20width%3D%221024px%22%20height%3D%221024px%22%20viewBox%3D%220%200%2024%2024%22%20version%3D%221.1%22%3E%3Ctitle%3Esplash-logo%3C%2Ftitle%3E%3Cg%20id%3D%22splash-logo%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill-rule%3D%22evenodd%22%3E%3Cpath%20d%3D%22M7.03766%2022.32C7.11099%2022.2133%207.65765%2021.5667%208.25096%2020.8667C8.85095%2020.1734%209.61093%2019.2534%209.95092%2018.8334C10.2842%2018.4134%2010.8242%2017.7668%2011.1442%2017.4001C11.9375%2016.4868%2012.1442%2016.1668%2012.0309%2016.0468C11.8642%2015.8868%2011.4375%2015.8335%2010.2842%2015.8335C9.23094%2015.8335%206.59101%2016.0468%206.43101%2016.1468C6.39101%2016.1735%206.38435%2016.1535%206.41768%2016.1068C6.57101%2015.8535%207.87097%2015.1669%208.1843%2015.1669C8.25763%2015.1669%208.31763%2015.1402%208.31763%2015.1002C8.31763%2015.0669%208.2443%2015.0335%208.15763%2015.0335C8.0643%2015.0335%207.74431%2014.9535%207.43765%2014.8602C6.60434%2014.6002%205.6177%2014.0869%206.15102%2014.1936C7.62431%2014.4869%209.85759%2014.5335%2010.9842%2014.3069C12.1975%2014.0536%2012.5575%2013.7936%2013.0042%2012.8536C13.2042%2012.4269%2013.2175%2012.3536%2013.2108%2011.7003C13.1975%209.82701%2012.1442%206.36044%2010.7376%203.54051C10.4642%203.00053%2010.2576%202.53387%2010.2842%202.51387C10.4176%202.38055%2011.5242%203.22719%2012.5242%204.23383C14.3841%206.11378%2015.9241%208.78037%2016.6241%2011.3403C16.7841%2011.9336%2016.7907%2012.0403%2016.7307%2012.3869C16.6707%2012.6936%2016.4774%2013.2003%2016.2707%2013.5736C16.2574%2013.5936%2016.4907%2013.6469%2016.7841%2013.6869C17.3574%2013.7669%2017.5774%2013.8602%2017.9107%2014.1402C18.2574%2014.4269%2018.284%2014.5402%2018.0707%2014.7402C17.724%2015.0602%2017.0041%2015.3669%2016.1574%2015.5469C15.9308%2015.5935%2015.8908%2015.6335%2015.7841%2015.9135C15.5574%2016.5135%2014.9108%2017.3735%2014.0175%2018.2734C12.9842%2019.3134%2010.7776%2021.16%2010.0576%2021.5867C9.14427%2022.1333%207.99764%2022.5%207.21766%2022.5H6.911L7.03766%2022.32Z%22%20fill%3D%22%23000%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");
        background-repeat: repeat repeat;
        background-position: center center;
        background-size: contain;
        transform: rotate(0);
      `;
    default:
      return `
        width: ${size}px;
        height: ${size * 0.4}px;
        background-color: ${color};
      `;
  }
};

const ConfettiItem = styled('div')<{
  index: number;
  rate: number;
}>`
  ${({ index, rate }) => {
    const w = randomNum(8) + 4;
    const l = randomNum(100);
    const colors = ['#0072ED', '#B4D8FF', '#F78598', '#5FD58B'];
    const shapes = ['rectangle', 'circle', 'triangle', 'swyftxBird', 'wavy'];
    const shape = shapes[randomNum(shapes.length)];
    const color = colors[randomNum(colors.length)];

    return `
      left: ${l}%;
      position: absolute;
      top: -10%;
      opacity: 1;
      transform: rotate(${randomNum(360)}deg);
      animation: drop-${index} ${rate + Math.random()}s ${Math.random()}s infinite;
      animation-delay: ${Math.random() * rate}s;

      ${shapeStyle(shape, w, color)}

      @keyframes drop-${index} {
        100% {
          top: 60%;
          left: ${l + randomNum(30)}%;
          opacity: 0;
        }
      }
    `;
  }}
`;

export { Confetti };
